/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, ComponentProps, RefObject } from 'preact';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'preact/hooks';

import { EnvironmentProvider } from '../UNSAFE_Environment';
import {
  FocusableComponentHandle,
  useMessageFocusManager
} from '../hooks/PRIVATE_useMessagesFocusManager';
import { useBreakpointValues } from '../hooks/UNSAFE_useBreakpointValues';
import { useMessagesContext } from '../hooks/UNSAFE_useMessagesContext';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { getRendererWithoutIndex, Message, MessagesManager } from '../PRIVATE_Message';
import { MessageLayer } from '../PRIVATE_MessageLayer';
import { BundleType } from '../resources/nls/bundle';
import { Flex } from '../UNSAFE_Flex';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { stringLiteralArray } from '../utils/UNSAFE_arrayUtils';
import { Item, ItemMetadata } from '../utils/UNSAFE_dataProvider';
import { Size } from '../utils/UNSAFE_size';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { MessageToastRedwoodTheme } from './themes/redwood/MessageToastTheme';

// TODO: refactor types so we don't repeat them here and MessageLayer
// 
const positions = stringLiteralArray([
  'top',
  'top-left',
  'top-right',
  'top-start',
  'top-end',
  'bottom',
  'bottom-left',
  'bottom-right',
  'bottom-start',
  'bottom-end'
]);

type Position = typeof positions[number];

type Offset =
  | number
  | {
      horizontal?: number;
      vertical?: number;
    };

// TODO: Refactor Message Item types
// 

/**
 * An object representing a single message in MessageToast component.
 */
export type MessageToastItem = {
  /**
   * Defines whether or not to include the close icon for the message
   *
   * @default 'on'
   */
  closeAffordance?: 'on' | 'off';

  /**
   * Defines the detail text of the message
   */
  detail?: string;

  /**
   * Defines the severity of the message
   *
   * @default 'error'
   */
  severity?: 'none' | 'error' | 'warning' | 'confirmation' | 'info';

  /**
   * Defines the sound to be played when opening the message
   *
   * @default 'none'
   */
  sound?: 'default' | 'none' | string;

  /**
   * Defines the primary text of the message
   */
  summary?: string;

  /**
   * Defines the time after which the message is to be closed automatically
   *
   * on - default of 5s
   * off - no auto timeout, manually dismissed
   * number - seconds in milliseconds format
   *
   * @default 'off'
   */
  autoTimeout?: 'on' | 'off' | number;
};

/**
 * Structure of data item passed to the renderers
 */
export type MessageToastRendererDataItem<K, D> = {
  /**
   * The data for the current message
   */
  data: D;

  /**
   * The key for the current message
   */
  key: K;

  /**
   * The metadata for the current message
   */
  metadata?: ItemMetadata<K>;
};

type Props<Key, Data> = {
  /**
   * Defines the fixed position where the notifications will to be placed on the screen.
   * - top-start resolves to top-left in LTR (top-right in RTL)
   * - top-end resolves to top-right in LTR (top-left in RTL)
   * - bottom-start resolves to bottom-left in LTR (bottom-right in RTL)
   * - bottom-end resolves to bottom-right in LTR (bottom-left in RTL)
   *
   * default bottom
   */
  position?: Position;

  /**
   * Defines an offset (in pixels) in the placement. Value can be supplied as a number or
   * an object with horizontal and vertical values.
   *
   * default 0
   */
  offset?: Offset;

  /**
   * Data for the MessageToast component. This data is used for rendering each toast message.
   * The key for each message will be configured using the key of the corresponding item. This way,
   * the component will know whether a new message is being added or an existing message is being updated/removed
   * when the new data comes in.
   */
  data: Item<Key, Data>[];

  /**
   * Triggered when a user tries to close a message through UI interaction. The application
   * should listen to this event and remove the corresponding message item from the data
   * which would then result in the message closed. If the application
   * fails to remove the message item from the data, then no change will be done in the
   * UI by the component and the message will stay in the UI opened.
   */
  onClose?: (item: Item<Key, Data>) => void;

  /**
   * Applications can use this property to provide the key of a renderer or a function that
   * returns the key of a renderer to use for rendering the detail content.
   *
   * When a renderer key is provided as a value for this property, the corresponding renderer
   * will be used for rendering the detail content for all the messages. If applications want
   * to use a different renderer for different messages, they can provide a function that
   * returns a renderer key instead.
   *
   * The provided function should accept an Item and return a key to a renderer for
   * rendering the corresponding message's detail content. The value returned from this function
   * should be a key to one of the renderers provided. If the returned value is not
   * one of the keys of the provided renderers, the component will throw an Error.
   *
   * If the function returns undefined, the component then will perform default rendering
   * of the detail content using the detail property of the corresponding message.
   *
   * If an application specifies both detail and a valid detailRendererKey, the detailRendererKey will
   * take precedence and the corresponding renderer will be used for rendering the detail content.
   */
  detailRendererKey?: string | ((item: Item<Key, Data>) => string | undefined);

  /**
   * Applications can use this property to provide the key of a renderer or a function that
   * returns the key of the renderer to use for rendering the start icon.
   *
   * When a renderer key is provided as a value for this property, the corresponding renderer
   * will be used for rendering the start icon for all the messages. If applications want
   * to use a different renderer for different messages, they can provide a function that
   * returns a renderer key instead.
   *
   * The provided function should accept an Item and return a key to a renderer for
   * rendering the corresponding message's start icon. The value returned from this function
   * should be a key to one of the renderers provided. If the returned value is not
   * one of the keys of the provided renderers, the component will throw an Error.
   *
   * If the function returns undefined, the component then will perform default rendering
   * of the start icon using the severity property of the corresponding message.
   *
   * If an application specifies both severity and a valid iconRendererKey, the iconRendererKey will
   * take precedence and the corresponding renderer will be used for rendering the start icon.
   */
  iconRendererKey?: string | ((item: Item<Key, Data>) => string | undefined);

  /**
   * A set of available renderers for rendering the message content. Which renderer is used
   * for rendering which content will be decided by specific properties in the row data.
   */
  renderers?: Record<string, (data: MessageToastRendererDataItem<Key, Data>) => ComponentChildren>;
};

/**
 * Animation states for toast messages
 * TODO: Get this from theme context provider
 */
const animationStates: ComponentProps<typeof MessagesManager>['animationStates'] = {
  entering: (node) => ({
    from: {
      transform: `translate(0, ${node.offsetHeight}px)`,
      opacity: 1
    },
    to: {
      transform: 'translate(0, 0)',
      opacity: 1
    },
    end: {
      transform: 'unset',
      opacity: 1
    },
    options: {
      duration: 250
    }
  }),
  exiting: {
    from: {
      opacity: 1
    },
    to: {
      opacity: 0
    },
    end: {
      opacity: 0
    },
    options: {
      duration: 250
    }
  }
};
const initialAnimationStyles = {
  opacity: 0
} as const;

export function MessageToast<K extends string | number, D extends MessageToastItem>({
  data,
  detailRendererKey,
  iconRendererKey,
  renderers,
  onClose,
  offset,
  position
}: Props<K, D>) {
  const messagesRef = useRef(new Map<K, HTMLDivElement | null>());
  const autoFocusRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement | null>;
  const containerDivRef = useRef<HTMLDivElement>(null);
  const focusHandleRef = useRef<FocusableComponentHandle>(null);
  const [liveRegionText, setLiveRegionText] = useState<string>();
  const [shouldRender, setShouldRender] = useState(data.length > 0);
  const [shouldTrapFocus, setShouldTrapFocus] = useState(false);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { UNSAFE_messagesLayerId } = useMessagesContext();

  // We need a ref that holds the current & previous data length, as the exiting
  // node will always call handleNextFocus with previous data.
  // As in TransitionGroup, when an item is removed from the data, a new vnode
  // will not be created instead previous vnode will be used. So, the new handleNextFocus
  // will not be called when the old vnode exits. Thus, we will be using a ref
  // to always get the correct current data length.
  const dataLengthRef = useRef(data.length);
  const prevDataLengthRef = useRef(0);
  // Update the data length ref
  dataLengthRef.current = data.length;

  // Update the focusHandleRef
  useImperativeHandle(
    focusHandleRef,
    () => ({
      focus: () => {
        // Only trigger focus if the component is rendering messages
        if (data.length) {
          // set the autoFocus ref to the first message and enable the focus trap
          // this will automatically focus the first message
          autoFocusRef.current = messagesRef.current.get(data[0].key) ?? null;
          setShouldTrapFocus(true);
          return true;
        }
        return false;
      },
      contains: (element: Element) => {
        // Only invoke method if the component is rendering messages
        if (data.length && element) {
          return containerDivRef.current?.contains(element) ?? false;
        }
        return false;
      }
    }),
    [data]
  );

  // Register handlers for focus management
  const { controller, handlers } = useMessageFocusManager(
    focusHandleRef,
    {
      onFocus: useCallback(() => {
        setLiveRegionText(translations.message_navigationFromMessagesRegion());
      }, [translations]),
      onFocusLeave: useCallback(() => {
        // disable trap which should revert the focus back to the last focused item
        setShouldTrapFocus(false);
      }, [])
    },
    {
      // we do not want the focus manager to handle Esc key as we handle it using the
      // FocusTrap here
      handleEscapeKey: false
    }
  );

  /**
   * Handles focus when a message is closed and animated away from the DOM
   * @param key The key of the message
   * @param index The index of the message
   */
  const handleNextFocus = useCallback(
    (_key: K, index: number, closedMessageNode?: Element) => {
      const isClosedMessageFocused = closedMessageNode?.contains(document.activeElement);

      // If there are no messages, do not render anything. As the old messages
      // are still in the DOM, use the data count to determine what to do next as it
      // represents the next state.
      if (dataLengthRef.current === 0) {
        setShouldRender(false);
        setShouldTrapFocus(false);
        // If the current message holds focus, then restore previous focus
        if (isClosedMessageFocused) {
          // As we have FocusTrap, trying to restore focus now will simply result in focus being pulled
          // back again. So, we restore the focus in the next tick so that the rendering will have been
          // done in the microtask.
          setImmediate(() => controller.restorePriorFocus());
        }
        return;
      }

      // In TransitionGroup, when an item is removed from the data, a new vnode
      // will not be created instead previous vnode will be used. So, the new handleNextFocus
      // will not be called when the old vnode exits. Thus, all the deps will not be pointing to
      // the current references, rather they will be pointing to the ones where this vnode
      // last existed in the data.
      // This means, the data will contain the closing message as well. But, all the ref objects
      // still point to the most up-to-date values. With that in mind, we will be evaluating the
      // following values.
      const renderedMessagesCount = data.length;

      // Now that this message is closed, focus the next message that will take this index. If no
      // message will take this message's index, then it means that this is the last message. If
      // that is the case, focus the message at the previous index.
      // Use the count of the messages that are currently shown in the UI (current state including
      // the message that will be removed). This way we can get the correct item from the data
      // as it will contain the closing message as well.
      const nextMessageIndexToFocus = index + 1 < renderedMessagesCount ? index + 1 : index - 1;

      // if next message is available then transfer the focus to the next element
      if (nextMessageIndexToFocus > -1 && isClosedMessageFocused) {
        const nextMessageKey = data[nextMessageIndexToFocus].key;
        messagesRef.current.get(nextMessageKey)?.focus();
      }
    },
    [controller, data]
  );

  // We will be using a function based ref to store the refs in the Map
  const setHTMLDivElement = useCallback(
    // TODO: use ramda.curry here (currently it is throwing ts error when used with generics)
    (key: K) => {
      return (ref: HTMLDivElement | null) => messagesRef.current.set(key, ref);
    },
    []
  );
  // Prioritize this component whenever the data changes and
  // the new data has at least one message
  useEffect(() => {
    const previousDataLength = prevDataLengthRef.current;
    prevDataLengthRef.current = data.length;

    // if there are messages that need to be rendered now, then do the following deeds:
    // 1. set the shouldRender flag
    // 2. set the live region
    // 3. prioritize this message component to be the latest
    if (data.length) {
      // set state to render content whenever the data is not empty
      setShouldRender(true);
      if (data.length > previousDataLength) {
        // Only when having a new message, update the aria-live area with the
        // text to indicate how to get the focus to the new message.
        setLiveRegionText(translations.message_navigationToMessagesRegion());
      }
      controller.prioritize();
      return;
    }

    // if there were messages and now removed, then set the live region to
    // announce this information
    if (previousDataLength) {
      setLiveRegionText(translations.messageToast_allMessagesClosed());
      return;
    }

    // When there are no messages, clear the live region so that
    // the navigation text will be read when a new message appear
    setLiveRegionText('');
  }, [controller, data.length, translations]);

  // determine responsive max-width
  const maxWidth = useBreakpointValues<Size>({
    sm: '--oj-c-PRIVATE-DO-NOT-USE-message-toast-max-width-sm',
    md: '--oj-c-PRIVATE-DO-NOT-USE-message-toast-max-width-md',
    lg: '--oj-c-PRIVATE-DO-NOT-USE-message-toast-max-width-lg'
  });
  // without offset, the messages will be hugging the vertical edges of the screen
  // so we will set a default offset.vertical value (any offset from the props will be
  // added to this).
  const paddedOffset = useBreakpointValues<Offset>({
    sm: {
      horizontal: typeof offset === 'number' ? offset : offset?.horizontal ?? 0,
      vertical: 16 + (typeof offset === 'number' ? offset : offset?.vertical ?? 0)
    },
    lg: {
      horizontal: typeof offset === 'number' ? offset : offset?.horizontal ?? 0,
      vertical: 24 + (typeof offset === 'number' ? offset : offset?.vertical ?? 0)
    }
  });

  // TODO: move this to the individual components.
  // Cannot do this as of now, as doing do results in oj-message-banner not working.
  const messageTranslations = useMemo(
    () => ({
      close: translations.close(),
      confirmation: translations.confirmation(),
      error: translations.error(),
      info: translations.info(),
      warning: translations.warn()
    }),
    [translations]
  );

  const { classes } = useComponentTheme(MessageToastRedwoodTheme);

  // When both shouldRender flag is false and no data to render, do not render
  // anything except the live region
  if (!shouldRender && data.length === 0) {
    return <LiveRegion>{liveRegionText}</LiveRegion>;
  }

  // TODO - Message Toast shouldn't always render as 'dark', it should be the inverse of the root environment context
  // See JET-57590
  return (
    <EnvironmentProvider environment={{ colorScheme: 'dark' }}>
      <MessageLayer offset={paddedOffset} position={position}>
        <div
          class={classes}
          ref={containerDivRef}
          tabIndex={-1} // Need to add tabindex to make the container receive the focus events
          id={UNSAFE_messagesLayerId}
          {...handlers}>
          <FocusTrap autoFocusRef={autoFocusRef} isDisabled={!shouldTrapFocus}>
            <Flex direction="column" gap="1x" align="center" maxWidth={maxWidth}>
              <MessagesManager
                animationStates={animationStates}
                initialAnimationStyles={initialAnimationStyles}
                data={data}
                onMessageWillRemove={handleNextFocus}>
                {({ index, item }) => (
                  <Message
                    messageRef={setHTMLDivElement(item.key)}
                    item={item}
                    detailRenderer={getRendererWithoutIndex(item, detailRendererKey, renderers)}
                    iconRenderer={getRendererWithoutIndex(item, iconRendererKey, renderers)}
                    index={index}
                    key={item.key}
                    onClose={onClose}
                    translations={messageTranslations}
                    variant="toast"
                  />
                )}
              </MessagesManager>
            </Flex>
          </FocusTrap>
        </div>
      </MessageLayer>
      <LiveRegion>{liveRegionText}</LiveRegion>
    </EnvironmentProvider>
  );
}
